import * as React from 'react';

import { autoprefixer } from '../../material-ui/autoprefixer';

import { Hsv, hsvToRgb } from './utils';

const RECTANGLE_SIZE = 154;
const INDICATOR_SIZE = 6;
const RECTANGLE_BORDER_WIDTH = 1;

interface MoveCoords {
    clientX: number;
    clientY: number;
}

interface MoveVerticalCoord {
    clientY: number;
}

interface HsvSelectorProps extends React.Props<HsvSelector> {
    h: number;
    s: number;
    v: number;
    style?: React.CSSProperties;
    onChange?: (hsv: Hsv) => void;
    onDragChange?: (isMouseDown: boolean) => void;
}

export class HsvSelector extends React.Component<HsvSelectorProps, {}> {

    private svSquare: HTMLDivElement;
    private innerHRectangle: HTMLDivElement;

    render() {
        const styles = this.getStyles();

        return (
            <div style={Object.assign({}, styles.root, this.props.style)}>
                <div
                    style={styles.svSquare}
                    ref={(domElement) => this.svSquare = domElement}
                    onTouchStart={this.onSvDown}
                    onMouseDown={this.onSvDown}
                    data-test-id="color-picker-sv-square"
                >
                    <div style={styles.svIndicator} data-test-id="color-picker-sv-indicator" />
                </div>
                <div style={styles.outerHRectangle}>
                    <div
                        style={styles.innerHRectangle}
                        ref={(domElement) => this.innerHRectangle = domElement}
                        onTouchStart={this.onHDown}
                        onMouseDown={this.onHDown}
                        data-test-id="color-picker-h-rectangle"
                    />
                    <div style={styles.hIndicator} data-test-id="color-picker-h-indicator" />
                </div>
            </div>
        );
    }

    private onSvDown = (event: React.TouchEvent<{}> | React.MouseEvent<{}>) => {
        const { targetTouches } = event as React.TouchEvent<{}>;
        const { clientX, clientY } = targetTouches ? targetTouches[0] : event as React.MouseEvent<{}>;

        const { h, s, v, onChange } = this.props;

        const getNewSv = (clientX: number, clientY: number) => {
            const boundingClientRect = this.svSquare.getBoundingClientRect();
            let x = clientX - boundingClientRect.left;
            let y = clientY - boundingClientRect.top;

            x = Math.max(Math.min(x, RECTANGLE_SIZE - 1), 0);
            y = Math.max(Math.min(y, RECTANGLE_SIZE - 1), 0);

            return {
                s: x / (RECTANGLE_SIZE - 1),
                v: 1 - y / (RECTANGLE_SIZE - 1)
            };
        };

        const onMove = ({ clientX, clientY }: MoveCoords) => {
            const { s: newS, v: newV } = getNewSv(clientX, clientY);

            if (newS !== s || newV !== v) {
                if (onChange) {
                    onChange({ h, s: newS, v: newV });
                }
            }
        };

        const onTouchMove = (touchEvent: TouchEvent) => {
            onMove(touchEvent.targetTouches[0]);
            touchEvent.preventDefault();
        };

        const onMouseMove = (mouseEvent: MouseEvent) => onMove(mouseEvent);

        const onUp = () => {
            if (this.props.onDragChange) {
                this.props.onDragChange(false);
            }

            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('mousemove', onMouseMove);
            [ 'touchcancel', 'touchend', 'touchup', 'mouseup' ].forEach((eventName) => {
                document.removeEventListener(eventName, onUp);
            });
        };

        if (this.props.onDragChange) {
            this.props.onDragChange(true);
        }

        onMove({ clientX, clientY });
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('mousemove', onMouseMove);
        [ 'touchcancel', 'touchend', 'touchup', 'mouseup' ].forEach((eventName) => {
            document.addEventListener(eventName, onUp);
        });
    };

    private onHDown = (event: React.TouchEvent<{}> | React.MouseEvent<{}>) => {
        const { targetTouches } = event as React.TouchEvent<{}>;
        const { clientY } = targetTouches ? targetTouches[0] : event as React.MouseEvent<{}>;

        const { h, s, v, onChange } = this.props;

        const getNewH = (clientY: number) => {
            let y = clientY - this.innerHRectangle.getBoundingClientRect().top;

            y = Math.max(Math.min(y, RECTANGLE_SIZE - 1), 0);

            return 1 - y / (RECTANGLE_SIZE - 1);
        };

        const onMove = ({ clientY }: MoveVerticalCoord) => {
            const newH = getNewH(clientY);

            if (newH !== h) {
                if (onChange) {
                    onChange({ h: newH, s, v });
                }
            }
        };

        const onTouchMove = (touchEvent: TouchEvent) => {
            onMove(touchEvent.targetTouches[0]);
            touchEvent.preventDefault();
        };

        const onMouseMove = (mouseEvent: MouseEvent) => onMove(mouseEvent);

        const onUp = () => {
            if (this.props.onDragChange) {
                this.props.onDragChange(false);
            }

            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('mousemove', onMouseMove);
            [ 'touchcancel', 'touchend', 'touchup', 'mouseup' ].forEach((eventName) => {
                document.removeEventListener(eventName, onUp);
            });
        };

        if (this.props.onDragChange) {
            this.props.onDragChange(true);
        }

        onMove({ clientY });
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('mousemove', onMouseMove);
        [ 'touchcancel', 'touchend', 'touchup', 'mouseup' ].forEach((eventName) => {
            document.addEventListener(eventName, onUp);
        });
    };

    private getStyles() {
        const { h, s, v } = this.props;
        const rgbBaseColor = hsvToRgb({ h, s: 1, v: 1 });

        return {
            root: autoprefixer({
                display: 'flex'
            }),
            svSquare: {
                width: RECTANGLE_SIZE,
                height: RECTANGLE_SIZE,
                marginRight: 8,
                backgroundColor: `rgb(${rgbBaseColor.r}, ${rgbBaseColor.g}, ${rgbBaseColor.b})`,
                backgroundImage: 'linear-gradient('
                  + 'to top, '
                  + `rgb(   0,   0,   0), `
                  + `rgba(  0,   0,   0, 0)`
                  + '),'
                  + 'linear-gradient('
                  + 'to right, '
                  + `rgb( 255, 255, 255), `
                  + `rgba(255, 255, 255, 0)`
                  + ')',
                border: `${RECTANGLE_BORDER_WIDTH}px solid #D9D8D8`, /* TODO: Use color from some theme. */
                cursor: 'pointer'
            },
            svIndicator: {
                width: INDICATOR_SIZE,
                height: INDICATOR_SIZE,
                border: `1px solid #FFFFFF`, /* TODO: Use color from some theme. */
                borderRadius: INDICATOR_SIZE / 2,
                boxShadow: '0 0 1px #000000', /* TODO: Use color from some theme. */
                marginTop: -INDICATOR_SIZE / 2 + ((1 - v) * (RECTANGLE_SIZE - RECTANGLE_BORDER_WIDTH * 2)),
                marginLeft: -INDICATOR_SIZE / 2 + (s * (RECTANGLE_SIZE - RECTANGLE_BORDER_WIDTH * 2))
            },
            outerHRectangle: {
                width: 10,
                height: RECTANGLE_SIZE,
                border: `${RECTANGLE_BORDER_WIDTH}px solid #D9D8D8` /* TODO: Use color from some theme. */
            },
            innerHRectangle: {
                position: 'absolute',
                width: 10,
                height: RECTANGLE_SIZE,
                background: 'linear-gradient('
                  + 'to top, '
                  + 'rgb(255,   0,   0) 0%, '
                  + `rgb(255, 255,   0) ${100 / 6}%, `
                  + `rgb(  0, 255,   0) ${100 / 6 * 2}%, `
                  + `rgb(  0, 255, 255) ${100 / 6 * 3}%, `
                  + `rgb(  0,   0, 255) ${100 / 6 * 4}%, `
                  + `rgb(255,   0, 255) ${100 / 6 * 5}%, `
                  + 'rgb(255,   0,   0) 100%'
                  + ')',
                cursor: 'pointer'
            },
            hIndicator: {
                position: 'absolute',
                width: INDICATOR_SIZE,
                height: INDICATOR_SIZE,
                border: '1px solid #FFFFFF', /* TODO: Use color from some theme. */
                borderRadius: INDICATOR_SIZE / 2,
                boxShadow: '0 0 1px #000000', /* TODO: Use color from some theme. */
                pointerEvents: 'none',
                marginTop: -INDICATOR_SIZE / 2 + ((1 - h) * (RECTANGLE_SIZE - RECTANGLE_BORDER_WIDTH * 2)),
                marginLeft: INDICATOR_SIZE / 2 - 2
            }
        };
    }

}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/components/color-picker/HsvSelector.tsx
 **/